/**
 * End-to-end conversion tracking.
 *
 * Every conversion (form submission, phone click, WhatsApp click, email click,
 * booking click, guide download) is sent to two places:
 *   1. GA4 — for campaign attribution and Google-side reporting.
 *   2. conversion_events in the backend — so the admin dashboard can break
 *      leads down by page and location without depending on GA sampling.
 *
 * No personal data is stored here: only page, location, campaign and device.
 */

import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";

export type ConversionEventType =
  | "form_submit"
  | "phone_click"
  | "whatsapp_click"
  | "email_click"
  | "booking_click"
  | "lead_magnet";

const SESSION_KEY = "ifs_session_id";

function sessionId(): string | null {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

function device(): "mobile" | "tablet" | "desktop" {
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

/** Known location slugs we run pages for, longest first so matching is exact. */
const LOCATION_SLUGS = [
  "wimborne-bournemouth",
  "henley-on-thames",
  "fordingbridge",
  "southampton",
  "new-forest",
  "salisbury",
  "winchester",
  "lymington",
  "ringwood",
  "wimborne",
  "andover",
  "romsey",
];

/** Derive the location a conversion happened on, from the URL path. */
export function locationFromPath(path: string): string | null {
  const segments = path.toLowerCase().split("/").filter(Boolean);
  for (const slug of LOCATION_SLUGS) {
    if (segments.includes(slug)) return slug;
  }
  return null;
}

function campaignParams() {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
    };
  } catch {
    return { utm_source: null, utm_medium: null, utm_campaign: null };
  }
}

const truncate = (value: string | null | undefined, max: number) =>
  value ? value.slice(0, max) : null;

/**
 * Record a conversion. Fire-and-forget — never blocks or breaks the UI.
 */
export function trackConversion(
  eventType: ConversionEventType,
  options?: { sourceLabel?: string; page?: string },
) {
  const page = options?.page ?? window.location.pathname;
  const utm = campaignParams();

  // 1. GA4
  trackEvent(eventType === "form_submit" ? "generate_lead" : "conversion", {
    event_category: "conversion",
    event_label: options?.sourceLabel ?? eventType,
    conversion_type: eventType,
    page,
  });

  // 2. Backend log
  void supabase
    .from("conversion_events")
    .insert({
      event_type: eventType,
      page_path: truncate(page, 300)!,
      page_title: truncate(document.title, 300),
      location_slug: locationFromPath(page),
      source_label: truncate(options?.sourceLabel ?? null, 100),
      session_id: sessionId(),
      referrer: truncate(document.referrer || null, 500),
      utm_source: truncate(utm.utm_source, 150),
      utm_medium: truncate(utm.utm_medium, 150),
      utm_campaign: truncate(utm.utm_campaign, 200),
      device: device(),
    })
    .then(undefined, () => {});
}

let globalConversionTrackingReady = false;

/**
 * Auto-track phone, WhatsApp and mailto clicks anywhere on the site.
 * Form submissions and booking clicks are tracked explicitly at their source.
 */
export function initConversionTracking() {
  if (globalConversionTrackingReady) return;
  globalConversionTrackingReady = true;

  document.addEventListener(
    "click",
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;

      const href = anchor.getAttribute("href") || "";
      const label = anchor.dataset.conversionLabel;

      if (href.startsWith("tel:")) {
        trackConversion("phone_click", { sourceLabel: label ?? "tel_link" });
      } else if (href.includes("wa.me") || href.includes("whatsapp.com")) {
        trackConversion("whatsapp_click", { sourceLabel: label ?? "whatsapp" });
      } else if (href.startsWith("mailto:")) {
        trackConversion("email_click", { sourceLabel: label ?? "mailto_link" });
      }
    },
    true,
  );
}
